import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, Upload, Image, Video, Loader2, Save, Mail, Send } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ContentEditor = ({ content, onClose, onSave, sections = [] }) => {
  const [formData, setFormData] = useState({
    title: content?.title || '',
    composer: content?.composer || '',
    theater: content?.theater || '',
    category: content?.category || 'ÒPERA',
    season: content?.season || '24-25',
    image: content?.image || '',
    hero_image: content?.hero_image || '',
    trailer_url: content?.trailer_url || '',
    synopsis: content?.synopsis || '',
    synopsis_extended: content?.synopsis_extended || '',
    tags: content?.tags || [],
    cast: content?.cast || [],
    artistic_credits: content?.artistic_credits || [],
    product_info: content?.product_info || {
      theater_name: '',
      recording_year: '',
      duration: '',
      subtitles_version: ''
    },
    // New fields for premiere and status
    premiere_date: content?.premiere_date || '',
    status: content?.status || 'available',
    release_label: content?.release_label || 'DIGITAL PREMIERE',
    section_id: content?.section_id || '',
    // Live streaming fields
    is_live: content?.is_live || false,
    live_date: content?.live_date || '',
    live_time: content?.live_time || '',
    // Encore fields
    is_encore: content?.is_encore || false,
    encore_start_date: content?.encore_start_date || '',
    encore_end_date: content?.encore_end_date || '',
    // Special pricing conditions
    has_special_conditions: content?.has_special_conditions || false,
    // Materials for cinemas
    materials: content?.materials || {
      poster_web_url: '',
      poster_print_url: '',
      poster_thumbnail: '',
      trailer_download_url: '',
      images_url: '',
      synopsis_url: '',
      handout_url: '',
      press_release_url: '',
      social_media_assets_url: ''
    },
    // Private data (admin only)
    private_data: content?.private_data || {
      provider: '',
      client_number: '',
      product_reference: '',
      contract_type: '',
      contract_value: '',
      exhibition_start_date: '',
      exhibition_end_date: '',
      exhibition_indefinite: false,
      notes: ''
    }
  });
  
  const [newTag, setNewTag] = useState('');
  const [newCastMember, setNewCastMember] = useState({ performer: '', character: '' });
  const [newCredit, setNewCredit] = useState({ name: '', role: '' });
  const [activeTab, setActiveTab] = useState('basic');
  const [media, setMedia] = useState([]);
  
  // Notification state
  const [notifyOnSave, setNotifyOnSave] = useState(false);
  const [notifyLoading, setNotifyLoading] = useState(false);
  const [notifyResult, setNotifyResult] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (content?.id) {
      loadMedia(content.id);
    }
  }, [content?.id]);

  const loadMedia = async (contentId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/media/content/${contentId}`);
      if (res.ok) setMedia(await res.json());
    } catch (err) { console.error(err); }
  };

  const handleSave = async () => {
    setSaving(true);
    setNotifyResult(null);
    
    // Clean form data before sending - convert empty strings to null for numeric fields
    const cleanFormData = {
      ...formData,
      private_data: formData.private_data ? {
        ...formData.private_data,
        contract_value: formData.private_data.contract_value === '' ? null : 
                        (formData.private_data.contract_value ? parseFloat(formData.private_data.contract_value) : null)
      } : null,
      // Also clean other optional fields that might have empty strings
      section_id: formData.section_id || null,
      hero_image: formData.hero_image || null,
      trailer_url: formData.trailer_url || null,
      synopsis_extended: formData.synopsis_extended || null,
      live_date: formData.live_date || null,
      live_time: formData.live_time || null,
      encore_start_date: formData.encore_start_date || null,
      encore_end_date: formData.encore_end_date || null,
      theater: formData.theater || null
    };
    
    try {
      const url = content?.id 
        ? `${BACKEND_URL}/api/content/${content.id}`
        : `${BACKEND_URL}/api/content`;
      
      const res = await fetch(url, {
        method: content?.id ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(cleanFormData)
      });
      
      if (res.ok) {
        const savedContent = await res.json();
        const contentId = savedContent.id || content?.id;
        
        // If notifyOnSave is checked and we have a content ID, send notifications
        if (notifyOnSave && contentId) {
          setNotifyLoading(true);
          try {
            const notifyRes = await fetch(`${BACKEND_URL}/api/content/${contentId}/notify-cinemas`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' }
            });
            
            if (notifyRes.ok) {
              const notifyData = await notifyRes.json();
              setNotifyResult({ success: true, message: notifyData.message });
              // Wait a moment to show the result before closing
              setTimeout(() => {
                onSave();
                onClose();
              }, 2000);
            } else {
              setNotifyResult({ success: false, message: 'Error al enviar notificaciones' });
              setTimeout(() => {
                onSave();
                onClose();
              }, 2000);
            }
          } catch (notifyErr) {
            console.error('Notification error:', notifyErr);
            setNotifyResult({ success: false, message: 'Error al enviar notificaciones' });
            setTimeout(() => {
              onSave();
              onClose();
            }, 2000);
          }
          setNotifyLoading(false);
        } else {
          onSave();
          onClose();
        }
      }
    } catch (err) { console.error(err); }
    setSaving(false);
  };

  // Function to send notification manually for existing content
  const handleNotifyOnly = async () => {
    if (!content?.id) return;
    
    setNotifyLoading(true);
    setNotifyResult(null);
    try {
      const notifyRes = await fetch(`${BACKEND_URL}/api/content/${content.id}/notify-cinemas`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      const notifyData = await notifyRes.json();
      
      if (notifyRes.ok) {
        setNotifyResult({ 
          success: true, 
          message: notifyData.message + (notifyData.error_count > 0 ? ` (${notifyData.error_count} fallidos)` : '')
        });
      } else {
        setNotifyResult({ 
          success: false, 
          message: notifyData.detail || 'Error al enviar notificaciones'
        });
      }
    } catch (err) {
      console.error('Notification error:', err);
      setNotifyResult({ success: false, message: 'Error de conexión al enviar notificaciones' });
    }
    setNotifyLoading(false);
  };

  const addTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData({ ...formData, tags: [...formData.tags, newTag.trim()] });
      setNewTag('');
    }
  };

  const removeTag = (tag) => {
    setFormData({ ...formData, tags: formData.tags.filter(t => t !== tag) });
  };

  const addCastMember = () => {
    if (newCastMember.performer.trim()) {
      setFormData({ ...formData, cast: [...formData.cast, { ...newCastMember }] });
      setNewCastMember({ performer: '', character: '' });
    }
  };

  const removeCastMember = (idx) => {
    setFormData({ ...formData, cast: formData.cast.filter((_, i) => i !== idx) });
  };

  const addCredit = () => {
    if (newCredit.name.trim()) {
      setFormData({ ...formData, artistic_credits: [...formData.artistic_credits, { ...newCredit }] });
      setNewCredit({ name: '', role: '' });
    }
  };

  const removeCredit = (idx) => {
    setFormData({ ...formData, artistic_credits: formData.artistic_credits.filter((_, i) => i !== idx) });
  };

  const handleMediaUpload = async (type) => {
    if (!content?.id) return alert('Guarda el contenido primero');
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = type === 'video' ? 'video/*' : 'image/*';
    input.multiple = true;
    
    input.onchange = async (e) => {
      const files = Array.from(e.target.files);
      setUploading(true);
      
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('content_id', content.id);
        formData.append('media_type', type);
        
        await fetch(`${BACKEND_URL}/api/media/upload`, { method: 'POST', body: formData });
      }
      
      setUploading(false);
      loadMedia(content.id);
    };
    input.click();
  };

  const deleteMedia = async (mediaId) => {
    await fetch(`${BACKEND_URL}/api/media/${mediaId}`, { method: 'DELETE' });
    loadMedia(content.id);
  };

  const tabs = [
    { id: 'basic', label: 'Información' },
    { id: 'synopsis', label: 'Sinopsis' },
    { id: 'cast', label: 'Reparto' },
    { id: 'credits', label: 'Ficha' },
    { id: 'product', label: 'Producción' },
    { id: 'materials', label: 'Materiales' },
    { id: 'media', label: 'Multimedia' },
    { id: 'private', label: 'Privado' }
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/80" onClick={onClose} />
      
      <div className="relative w-full max-w-4xl max-h-[90vh] bg-[#1a1a1a] rounded-xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <h2 className="text-white text-xl font-bold">
            {content?.id ? 'Editar contenido' : 'Nuevo contenido'}
          </h2>
          <button onClick={onClose} className="p-2 text-white/60 hover:text-white hover:bg-white/10 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 p-2 border-b border-white/10 overflow-x-auto">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap ${
                activeTab === tab.id ? 'bg-[#e4002b] text-white' : 'text-white/60 hover:bg-white/10'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Basic Info */}
          {activeTab === 'basic' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-white/60 text-sm mb-1">Tags (Ópera, título, compositor, teatro)</label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {formData.tags.map((tag, i) => (
                    <span key={i} className="px-3 py-1 bg-[#e4002b]/20 text-[#e4002b] rounded-full text-sm flex items-center gap-1">
                      {tag}
                      <button onClick={() => removeTag(tag)} className="hover:text-white"><X className="w-3 h-3" /></button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Añadir tag..."
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                    className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded text-white text-sm"
                  />
                  <button onClick={addTag} className="px-4 py-2 bg-white/10 text-white rounded text-sm hover:bg-white/20">
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-white/60 text-sm mb-1">Título *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded text-white"
                />
              </div>

              <div>
                <label className="block text-white/60 text-sm mb-1">Compositor / Director</label>
                <input
                  type="text"
                  value={formData.composer}
                  onChange={(e) => setFormData({ ...formData, composer: e.target.value })}
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded text-white"
                />
              </div>

              <div>
                <label className="block text-white/60 text-sm mb-1">Teatro</label>
                <input
                  type="text"
                  value={formData.theater}
                  onChange={(e) => setFormData({ ...formData, theater: e.target.value })}
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded text-white"
                />
              </div>

              <div>
                <label className="block text-white/60 text-sm mb-1">Categoría</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded text-white"
                >
                  <option value="ÒPERA">Ópera</option>
                  <option value="BALLET">Ballet</option>
                  <option value="CONCERT">Concierto</option>
                  <option value="DOCUMENTAL">Documental</option>
                  <option value="PELÍCULA">Película</option>
                </select>
              </div>

              <div>
                <label className="block text-white/60 text-sm mb-1">Temporada</label>
                <select
                  value={formData.season}
                  onChange={(e) => setFormData({ ...formData, season: e.target.value })}
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded text-white"
                >
                  <option value="24-25">Temporada 24-25</option>
                  <option value="25-26">Temporada 25-26</option>
                </select>
              </div>

              <div>
                <label className="block text-white/60 text-sm mb-1">URL Trailer</label>
                <input
                  type="text"
                  value={formData.trailer_url}
                  onChange={(e) => setFormData({ ...formData, trailer_url: e.target.value })}
                  placeholder="https://youtube.com/watch?v=..."
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded text-white"
                />
              </div>

              <div>
                <label className="block text-white/60 text-sm mb-1">URL Imagen principal</label>
                <input
                  type="text"
                  value={formData.image}
                  onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded text-white"
                />
              </div>

              <div>
                <label className="block text-white/60 text-sm mb-1">URL Imagen hero</label>
                <input
                  type="text"
                  value={formData.hero_image}
                  onChange={(e) => setFormData({ ...formData, hero_image: e.target.value })}
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded text-white"
                />
              </div>

              {/* Section Assignment */}
              <div>
                <label className="block text-white/60 text-sm mb-1">Sección</label>
                <select
                  value={formData.section_id}
                  onChange={(e) => setFormData({ ...formData, section_id: e.target.value })}
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded text-white"
                >
                  <option value="">Sin asignar</option>
                  {sections.map(s => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>

              {/* Premiere Info */}
              <div>
                <label className="block text-white/60 text-sm mb-1">Fecha de estreno</label>
                <input
                  type="date"
                  value={formData.premiere_date}
                  onChange={(e) => setFormData({ ...formData, premiere_date: e.target.value })}
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded text-white"
                />
              </div>

              <div>
                <label className="block text-white/60 text-sm mb-1">Etiqueta de lanzamiento</label>
                <input
                  type="text"
                  value={formData.release_label}
                  onChange={(e) => setFormData({ ...formData, release_label: e.target.value })}
                  placeholder="DIGITAL PREMIERE"
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded text-white"
                />
              </div>

              <div>
                <label className="block text-white/60 text-sm mb-1">Estado</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded text-white"
                >
                  <option value="available">Disponible</option>
                  <option value="coming_soon">Próximamente</option>
                  <option value="exclusive">Exclusivo</option>
                  <option value="new">Nuevo</option>
                </select>
              </div>

              {/* Live Stream Section */}
              <div className="md:col-span-2 mt-4 p-4 bg-[#e4002b]/10 border border-[#e4002b]/30 rounded-lg">
                <div className="flex items-center gap-3 mb-4">
                  <input
                    type="checkbox"
                    id="is_live"
                    checked={formData.is_live}
                    onChange={(e) => setFormData({ ...formData, is_live: e.target.checked, is_encore: e.target.checked ? false : formData.is_encore })}
                    className="w-5 h-5 rounded accent-[#e4002b]"
                  />
                  <label htmlFor="is_live" className="text-white font-medium cursor-pointer">
                    Es un DIRECTO (evento en vivo)
                  </label>
                </div>
                
                {formData.is_live && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <div>
                      <label className="block text-white/60 text-sm mb-1">Fecha del directo</label>
                      <input
                        type="date"
                        value={formData.live_date}
                        onChange={(e) => setFormData({ ...formData, live_date: e.target.value })}
                        className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-white/60 text-sm mb-1">Hora del directo</label>
                      <input
                        type="time"
                        value={formData.live_time}
                        onChange={(e) => setFormData({ ...formData, live_time: e.target.value })}
                        className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded text-white"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Encore Section */}
              <div className="md:col-span-2 mt-4 p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                <div className="flex items-center gap-3 mb-4">
                  <input
                    type="checkbox"
                    id="is_encore"
                    checked={formData.is_encore}
                    onChange={(e) => setFormData({ ...formData, is_encore: e.target.checked, is_live: e.target.checked ? false : formData.is_live })}
                    className="w-5 h-5 rounded accent-amber-500"
                  />
                  <label htmlFor="is_encore" className="text-white font-medium cursor-pointer">
                    Es un ENCORE (reprogramación con horquilla de fechas)
                  </label>
                </div>
                
                {formData.is_encore && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <div>
                      <label className="block text-white/60 text-sm mb-1">Fecha inicio disponibilidad</label>
                      <input
                        type="date"
                        value={formData.encore_start_date}
                        onChange={(e) => setFormData({ ...formData, encore_start_date: e.target.value })}
                        className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded text-white"
                      />
                      <p className="text-white/40 text-xs mt-1">El cine podrá contratar desde esta fecha</p>
                    </div>
                    <div>
                      <label className="block text-white/60 text-sm mb-1">Fecha fin disponibilidad</label>
                      <input
                        type="date"
                        value={formData.encore_end_date}
                        onChange={(e) => setFormData({ ...formData, encore_end_date: e.target.value })}
                        className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded text-white"
                      />
                      <p className="text-white/40 text-xs mt-1">El cine podrá contratar hasta esta fecha</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Special Pricing Conditions */}
              <div className="md:col-span-2 bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="has_special_conditions"
                    checked={formData.has_special_conditions}
                    onChange={(e) => setFormData({ ...formData, has_special_conditions: e.target.checked })}
                    className="w-5 h-5 rounded border-white/30 bg-white/10 text-amber-500"
                  />
                  <label htmlFor="has_special_conditions" className="text-white font-medium cursor-pointer">
                    Condiciones económicas especiales
                  </label>
                </div>
                <p className="text-white/50 text-sm mt-2 ml-8">
                  Marcar si este contenido requiere negociación de condiciones individuales con cada cine. 
                  El cine deberá consultar las condiciones antes de contratar.
                </p>
              </div>
            </div>
          )}

          {/* Synopsis */}
          {activeTab === 'synopsis' && (
            <div className="space-y-4">
              <div>
                <label className="block text-white/60 text-sm mb-1">Sinopsis</label>
                <textarea
                  value={formData.synopsis}
                  onChange={(e) => setFormData({ ...formData, synopsis: e.target.value })}
                  rows={5}
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded text-white"
                />
              </div>
              <div>
                <label className="block text-white/60 text-sm mb-1">Sinopsis extendida</label>
                <textarea
                  value={formData.synopsis_extended}
                  onChange={(e) => setFormData({ ...formData, synopsis_extended: e.target.value })}
                  rows={8}
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded text-white"
                />
              </div>
            </div>
          )}

          {/* Cast */}
          {activeTab === 'cast' && (
            <div className="space-y-4">
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="Nombre del artista"
                  value={newCastMember.performer}
                  onChange={(e) => setNewCastMember({ ...newCastMember, performer: e.target.value })}
                  className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded text-white"
                />
                <input
                  type="text"
                  placeholder="Personaje / Rol"
                  value={newCastMember.character}
                  onChange={(e) => setNewCastMember({ ...newCastMember, character: e.target.value })}
                  className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded text-white"
                />
                <button onClick={addCastMember} className="px-4 py-2 bg-[#e4002b] text-white rounded">
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              
              <div className="space-y-2">
                {formData.cast.map((member, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-white/5 rounded">
                    <div>
                      <span className="text-white font-medium">{member.performer}</span>
                      <span className="text-white/50 ml-2">como {member.character}</span>
                    </div>
                    <button onClick={() => removeCastMember(i)} className="p-1 text-red-400 hover:bg-red-500/10 rounded">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Artistic Credits */}
          {activeTab === 'credits' && (
            <div className="space-y-4">
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="Nombre"
                  value={newCredit.name}
                  onChange={(e) => setNewCredit({ ...newCredit, name: e.target.value })}
                  className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded text-white"
                />
                <input
                  type="text"
                  placeholder="Rol (Director, Escenografía...)"
                  value={newCredit.role}
                  onChange={(e) => setNewCredit({ ...newCredit, role: e.target.value })}
                  className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded text-white"
                />
                <button onClick={addCredit} className="px-4 py-2 bg-[#e4002b] text-white rounded">
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              
              <div className="space-y-2">
                {formData.artistic_credits.map((credit, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-white/5 rounded">
                    <div>
                      <span className="text-white font-medium">{credit.name}</span>
                      <span className="text-white/50 ml-2">- {credit.role}</span>
                    </div>
                    <button onClick={() => removeCredit(i)} className="p-1 text-red-400 hover:bg-red-500/10 rounded">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Product Info */}
          {activeTab === 'product' && (
            <div className="space-y-6">
              {/* Campos de Emisión - Solo para Directos */}
              {formData.is_live && (
                <div className="p-4 bg-[#e4002b]/10 border border-[#e4002b]/30 rounded">
                  <h3 className="text-[#e4002b] font-bold text-sm uppercase tracking-wider mb-4">Emisión del Directo</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-white/60 text-sm mb-1">Día de emisión</label>
                      <input
                        type="date"
                        value={formData.live_date || ''}
                        onChange={(e) => setFormData({ ...formData, live_date: e.target.value })}
                        className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-white/60 text-sm mb-1">Hora de emisión</label>
                      <input
                        type="time"
                        value={formData.live_time || ''}
                        onChange={(e) => setFormData({ ...formData, live_time: e.target.value })}
                        className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded text-white"
                      />
                    </div>
                  </div>
                </div>
              )}
              
              {/* Información de producción estándar */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-white/60 text-sm mb-1">Nombre del teatro</label>
                  <input
                    type="text"
                    value={formData.product_info?.theater_name || ''}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      product_info: { ...formData.product_info, theater_name: e.target.value }
                    })}
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded text-white"
                  />
                </div>
                <div>
                  <label className="block text-white/60 text-sm mb-1">Año de grabación</label>
                  <input
                    type="text"
                    value={formData.product_info?.recording_year || ''}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      product_info: { ...formData.product_info, recording_year: e.target.value }
                    })}
                    placeholder="2024"
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded text-white"
                  />
                </div>
                <div>
                  <label className="block text-white/60 text-sm mb-1">Duración</label>
                  <input
                    type="text"
                    value={formData.product_info?.duration || ''}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      product_info: { ...formData.product_info, duration: e.target.value }
                    })}
                    placeholder="2h 30min"
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded text-white"
                  />
                </div>
                <div>
                  <label className="block text-white/60 text-sm mb-1">Versión con subtítulos</label>
                  <input
                    type="text"
                    value={formData.product_info?.subtitles_version || ''}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      product_info: { ...formData.product_info, subtitles_version: e.target.value }
                    })}
                    placeholder="Español, English, Français"
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded text-white"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Materials */}
          {activeTab === 'materials' && (
            <div className="space-y-6">
              <div className="p-4 bg-[#e4002b]/10 border border-[#e4002b]/30 rounded text-white/80 text-sm">
                Estos materiales estarán disponibles para descarga exclusiva de cines registrados y aprobados.
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Poster Web */}
                <div>
                  <label className="block text-white/60 text-sm mb-1">URL Cartel (Web)</label>
                  <input
                    type="text"
                    value={formData.materials?.poster_web_url || ''}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      materials: { ...formData.materials, poster_web_url: e.target.value }
                    })}
                    placeholder="https://..."
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded text-white"
                  />
                </div>

                {/* Poster Print */}
                <div>
                  <label className="block text-white/60 text-sm mb-1">URL Cartel (Imprenta)</label>
                  <input
                    type="text"
                    value={formData.materials?.poster_print_url || ''}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      materials: { ...formData.materials, poster_print_url: e.target.value }
                    })}
                    placeholder="https://..."
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded text-white"
                  />
                </div>

                {/* Poster Thumbnail */}
                <div>
                  <label className="block text-white/60 text-sm mb-1">URL Miniatura Cartel</label>
                  <input
                    type="text"
                    value={formData.materials?.poster_thumbnail || ''}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      materials: { ...formData.materials, poster_thumbnail: e.target.value }
                    })}
                    placeholder="https://... (opcional, usa imagen principal si vacío)"
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded text-white"
                  />
                </div>

                {/* Trailer Download */}
                <div>
                  <label className="block text-white/60 text-sm mb-1">URL Descarga Trailer</label>
                  <input
                    type="text"
                    value={formData.materials?.trailer_download_url || ''}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      materials: { ...formData.materials, trailer_download_url: e.target.value }
                    })}
                    placeholder="https://..."
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded text-white"
                  />
                </div>

                {/* Images */}
                <div>
                  <label className="block text-white/60 text-sm mb-1">URL Pack Imágenes</label>
                  <input
                    type="text"
                    value={formData.materials?.images_url || ''}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      materials: { ...formData.materials, images_url: e.target.value }
                    })}
                    placeholder="https://..."
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded text-white"
                  />
                </div>

                {/* Synopsis */}
                <div>
                  <label className="block text-white/60 text-sm mb-1">URL Sinopsis (PDF/DOC)</label>
                  <input
                    type="text"
                    value={formData.materials?.synopsis_url || ''}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      materials: { ...formData.materials, synopsis_url: e.target.value }
                    })}
                    placeholder="https://..."
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded text-white"
                  />
                </div>

                {/* Handout */}
                <div>
                  <label className="block text-white/60 text-sm mb-1">URL Hoja de mano</label>
                  <input
                    type="text"
                    value={formData.materials?.handout_url || ''}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      materials: { ...formData.materials, handout_url: e.target.value }
                    })}
                    placeholder="https://..."
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded text-white"
                  />
                </div>

                {/* Press Release */}
                <div>
                  <label className="block text-white/60 text-sm mb-1">URL Nota de prensa</label>
                  <input
                    type="text"
                    value={formData.materials?.press_release_url || ''}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      materials: { ...formData.materials, press_release_url: e.target.value }
                    })}
                    placeholder="https://..."
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded text-white"
                  />
                </div>

                {/* Social Media Assets */}
                <div className="md:col-span-2">
                  <label className="block text-white/60 text-sm mb-1">URL Assets RRSS</label>
                  <input
                    type="text"
                    value={formData.materials?.social_media_assets_url || ''}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      materials: { ...formData.materials, social_media_assets_url: e.target.value }
                    })}
                    placeholder="https://..."
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded text-white"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Media */}
          {activeTab === 'media' && (
            <div className="space-y-4">
              {!content?.id && (
                <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded text-yellow-400 text-sm">
                  Guarda el contenido primero para poder subir multimedia
                </div>
              )}
              
              <div className="flex gap-2">
                <button
                  onClick={() => handleMediaUpload('image')}
                  disabled={uploading || !content?.id}
                  className="flex items-center gap-2 px-4 py-2 bg-white/10 text-white rounded hover:bg-white/20 disabled:opacity-50"
                >
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Image className="w-4 h-4" />}
                  Subir Imágenes
                </button>
                <button
                  onClick={() => handleMediaUpload('video')}
                  disabled={uploading || !content?.id}
                  className="flex items-center gap-2 px-4 py-2 bg-white/10 text-white rounded hover:bg-white/20 disabled:opacity-50"
                >
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Video className="w-4 h-4" />}
                  Subir Videos
                </button>
              </div>

              {media.length > 0 && (
                <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
                  {media.map(m => (
                    <div key={m.id} className="relative group aspect-square bg-white/10 rounded overflow-hidden">
                      {m.type === 'image' ? (
                        <img src={`${BACKEND_URL}${m.url}`} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Video className="w-8 h-8 text-white/50" />
                        </div>
                      )}
                      <button
                        onClick={() => deleteMedia(m.id)}
                        className="absolute top-1 right-1 p-1 bg-red-500/80 text-white rounded opacity-0 group-hover:opacity-100"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Private Data (Admin Only) */}
          {activeTab === 'private' && (
            <div className="space-y-6">
              <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                <p className="text-amber-400 text-sm flex items-center gap-2">
                  <span className="text-lg">🔒</span>
                  Esta información es privada y solo visible para administradores.
                </p>
              </div>

              {/* Proveedor */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-white/60 text-sm mb-1">Proveedor</label>
                  <input
                    type="text"
                    value={formData.private_data.provider}
                    onChange={(e) => setFormData({
                      ...formData,
                      private_data: { ...formData.private_data, provider: e.target.value }
                    })}
                    placeholder="Nombre del proveedor"
                    className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded text-white placeholder:text-white/40"
                  />
                </div>
                <div>
                  <label className="block text-white/60 text-sm mb-1">Nº de Cliente</label>
                  <input
                    type="text"
                    value={formData.private_data.client_number}
                    onChange={(e) => setFormData({
                      ...formData,
                      private_data: { ...formData.private_data, client_number: e.target.value }
                    })}
                    placeholder="Número de cliente"
                    className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded text-white placeholder:text-white/40"
                  />
                </div>
                <div>
                  <label className="block text-white/60 text-sm mb-1">Nº Referencia Producto</label>
                  <input
                    type="text"
                    value={formData.private_data.product_reference}
                    onChange={(e) => setFormData({
                      ...formData,
                      private_data: { ...formData.private_data, product_reference: e.target.value }
                    })}
                    placeholder="Referencia del producto"
                    className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded text-white placeholder:text-white/40"
                  />
                </div>
              </div>

              {/* Términos contractuales */}
              <div className="border-t border-white/10 pt-4">
                <h3 className="text-white font-semibold mb-4">Términos Contractuales</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-white/60 text-sm mb-1">Tipo de contrato</label>
                    <select
                      value={formData.private_data.contract_type}
                      onChange={(e) => setFormData({
                        ...formData,
                        private_data: { ...formData.private_data, contract_type: e.target.value }
                      })}
                      className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded text-white"
                    >
                      <option value="">Seleccionar...</option>
                      <option value="fee">Fee (Importe fijo €)</option>
                      <option value="percentage">Porcentaje (%)</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-white/60 text-sm mb-1">
                      {formData.private_data.contract_type === 'percentage' ? 'Porcentaje (%)' : 'Importe (€)'}
                    </label>
                    <input
                      type="number"
                      step={formData.private_data.contract_type === 'percentage' ? '1' : '0.01'}
                      value={formData.private_data.contract_value}
                      onChange={(e) => setFormData({
                        ...formData,
                        private_data: { ...formData.private_data, contract_value: e.target.value }
                      })}
                      placeholder={formData.private_data.contract_type === 'percentage' ? '45' : '350.00'}
                      className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded text-white placeholder:text-white/40"
                    />
                  </div>
                </div>
              </div>

              {/* Ventana de exhibición */}
              <div className="border-t border-white/10 pt-4">
                <h3 className="text-white font-semibold mb-4">Ventana de Exhibición</h3>
                <div className="space-y-4">
                  <label className="flex items-center gap-2 text-white cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.private_data.exhibition_indefinite}
                      onChange={(e) => setFormData({
                        ...formData,
                        private_data: { ...formData.private_data, exhibition_indefinite: e.target.checked }
                      })}
                      className="w-4 h-4 rounded border-white/30 bg-white/10 text-[#e4002b] focus:ring-[#e4002b]"
                    />
                    Ventana de exhibición indefinida
                  </label>
                  
                  {!formData.private_data.exhibition_indefinite && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-white/60 text-sm mb-1">Fecha inicio</label>
                        <input
                          type="date"
                          value={formData.private_data.exhibition_start_date}
                          onChange={(e) => setFormData({
                            ...formData,
                            private_data: { ...formData.private_data, exhibition_start_date: e.target.value }
                          })}
                          className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded text-white"
                        />
                      </div>
                      <div>
                        <label className="block text-white/60 text-sm mb-1">Fecha fin</label>
                        <input
                          type="date"
                          value={formData.private_data.exhibition_end_date}
                          onChange={(e) => setFormData({
                            ...formData,
                            private_data: { ...formData.private_data, exhibition_end_date: e.target.value }
                          })}
                          className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded text-white"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Notas adicionales */}
              <div className="border-t border-white/10 pt-4">
                <label className="block text-white/60 text-sm mb-1">Notas adicionales</label>
                <textarea
                  value={formData.private_data.notes}
                  onChange={(e) => setFormData({
                    ...formData,
                    private_data: { ...formData.private_data, notes: e.target.value }
                  })}
                  placeholder="Notas internas sobre este contenido..."
                  rows={3}
                  className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded text-white placeholder:text-white/40"
                />
              </div>
            </div>
          )}
        </div>

        {/* Notification Result */}
        {notifyResult && (
          <div className={`mx-4 mb-2 p-3 rounded-lg ${notifyResult.success ? 'bg-green-500/20 border border-green-500/30' : 'bg-red-500/20 border border-red-500/30'}`}>
            <p className={`text-sm ${notifyResult.success ? 'text-green-400' : 'text-red-400'}`}>
              {notifyResult.message}
            </p>
          </div>
        )}

        {/* Footer */}
        <div className="p-4 border-t border-white/10 space-y-4">
          {/* Notification Options */}
          <div className="flex flex-wrap items-center justify-between gap-4 p-3 bg-white/5 rounded-lg border border-white/10">
            <div className="flex items-center gap-3">
              <Mail className="w-5 h-5 text-[#e4002b]" />
              <div>
                <p className="text-white text-sm font-medium">Notificar a cines</p>
                <p className="text-white/50 text-xs">Enviar email a todos los cines aprobados</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={notifyOnSave}
                  onChange={(e) => setNotifyOnSave(e.target.checked)}
                  className="w-4 h-4 rounded border-white/30 bg-white/10 text-[#e4002b] focus:ring-[#e4002b]"
                />
                <span className="text-white/80 text-sm">Al guardar</span>
              </label>
              {content?.id && (
                <button
                  onClick={handleNotifyOnly}
                  disabled={notifyLoading}
                  className="flex items-center gap-2 px-4 py-2 bg-[#e4002b]/20 text-[#e4002b] rounded hover:bg-[#e4002b]/30 disabled:opacity-50 text-sm font-medium"
                  data-testid="notify-cinemas-btn"
                >
                  {notifyLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                  Enviar ahora
                </button>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-2">
            <button onClick={onClose} className="px-6 py-2 bg-white/10 text-white rounded hover:bg-white/20">
              Cancelar
            </button>
            <button 
              onClick={handleSave} 
              disabled={saving || notifyLoading || !formData.title.trim()}
              className="flex items-center gap-2 px-6 py-2 bg-[#e4002b] text-white rounded hover:bg-[#c70024] disabled:opacity-50"
            >
              {(saving || notifyLoading) ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {notifyLoading ? 'Enviando...' : (notifyOnSave ? 'Guardar y notificar' : 'Guardar')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContentEditor;
